/**
 * Print my cart.
 *
 * `[data-print-cart]` (snippets/cart-summary.liquid, shown while checkout is
 * paused) prints the cart page layout. On the cart page that is a plain print.
 * Elsewhere (the cart drawer) the cart page markup is fetched into a hidden
 * `.cart-print-sheet` that only shows in print (sections/main-cart.liquid), so
 * the shopper stays put. A hidden iframe is not an option: Shopify serves
 * storefront pages with `X-Frame-Options: DENY`. Printing never changes the cart.
 */

import { ThemeEvents } from '@theme/events';

const SHEET_CLASS = 'cart-print-sheet';

/** Must exceed the quantity debounce in assets/component-cart-items.js (300 ms). */
const QUANTITY_SETTLE_MS = 400;
const SETTLE_TIMEOUT_MS = 10000;

/** @type {boolean} */
let busy = false;

let lastQuantityChangeAt = 0;

// Capture phase: record the edit before the click that blurred the input reaches the print handler.
document.addEventListener(
  ThemeEvents.quantitySelectorUpdate,
  () => {
    lastQuantityChangeAt = performance.now();
  },
  true
);

/**
 * Resolves once no quantity edit is waiting on the debounce and no cart change request is in flight
 * (component-cart-items.js flags those with `.cart-items-disabled`), so the printout matches the cart.
 * Gives up after SETTLE_TIMEOUT_MS so a stuck update cannot block printing.
 * @returns {Promise<void>}
 */
async function waitForCartToSettle() {
  const deadline = performance.now() + SETTLE_TIMEOUT_MS;
  const isPending = () =>
    performance.now() - lastQuantityChangeAt < QUANTITY_SETTLE_MS ||
    document.querySelector('cart-items-component.cart-items-disabled') !== null;

  while (isPending() && performance.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  // Let the morph from the last response paint before printing.
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

/**
 * Shopify links the stylesheet-tag bundle as media="screen", so component styles would not print.
 */
function enableComponentStylesInPrint() {
  for (const link of document.querySelectorAll('link[href*="/compiled_assets/styles.css"][media="screen"]')) {
    if (link instanceof HTMLLinkElement) link.media = 'all';
  }
}

/**
 * @param {ParentNode} root
 * @returns {Promise<void>}
 */
async function loadImages(root) {
  // Lazy images below the fold, or inside a hidden sheet, would otherwise print blank.
  const images = Array.from(root.querySelectorAll('.cart-items img, .cart-page__print-logo img')).filter(
    (image) => image instanceof HTMLImageElement
  );
  for (const image of images) image.loading = 'eager';
  await Promise.all(images.map((image) => image.decode().catch(() => {})));
}

function removeSheet() {
  for (const sheet of document.querySelectorAll(`body > .${SHEET_CLASS}`)) sheet.remove();
}

/**
 * @param {string} cartUrl
 * @returns {Promise<HTMLElement>}
 */
async function buildSheet(cartUrl) {
  const response = await fetch(cartUrl, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Failed to fetch cart page: ${response.status}`);
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

  const section = doc.querySelector('cart-items-component > .section');
  if (!section || !section.querySelector('.cart-page:not(.cart-page--empty)')) {
    throw new Error('Cart page markup not found');
  }

  // The live page already owns these ids (#checkout, #cart-form, ...).
  for (const element of section.querySelectorAll('[id]')) element.removeAttribute('id');
  for (const script of section.querySelectorAll('script, template')) script.remove();

  const sheet = document.createElement('div');
  sheet.className = SHEET_CLASS;
  sheet.inert = true;
  sheet.setAttribute('aria-hidden', 'true');
  sheet.append(document.importNode(section, true));
  return sheet;
}

/**
 * @param {HTMLButtonElement} trigger
 * @returns {Promise<void>}
 */
async function printCart(trigger) {
  if (busy) return;
  busy = true;
  trigger.disabled = true;
  trigger.setAttribute('aria-busy', 'true');

  try {
    await waitForCartToSettle();

    if (document.querySelector('main .cart-page')) {
      await loadImages(document);
      window.print();
      return;
    }

    const sheet = await buildSheet(trigger.dataset.cartUrl || '/cart');
    removeSheet();
    document.body.append(sheet);
    await loadImages(sheet);
    enableComponentStylesInPrint();
    window.addEventListener('afterprint', removeSheet, { once: true });
    window.print();
  } catch (error) {
    console.error('[print-cart]', error);
    removeSheet();
  } finally {
    busy = false;
    trigger.disabled = false;
    trigger.removeAttribute('aria-busy');
  }
}

// Delegated: the cart drawer and cart page morph on every update, replacing the button.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target?.closest('[data-print-cart]');
  if (!(trigger instanceof HTMLButtonElement) || trigger.disabled) return;

  event.preventDefault();
  printCart(trigger);
});
